import { useState, useEffect } from "react"
import TimeRangeInput from "./TimeRangeInput"
import { Button } from "@ui/Button"
import { Card } from "@ui/Card"
import { Input } from "@ui/Input"
import { Select } from "@ui/Select"

const DAYS = [ "sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday" ]
const DAY_LABELS = [ "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday" ]
const DURATIONS = [ 15, 30, 45, 60 ]
const BUFFERS = [ 0, 15, 30, 45 ]
const BUFFER_LABELS = { 0: "No buffer", 15: "15 min", 30: "30 min", 45: "45 min" }

export default function AvailabilityForm({ courseId }) {
  const [ config, setConfig ] = useState(null)
  const [ loading, setLoading ] = useState(true)
  const [ saving, setSaving ] = useState(false)

  const [ duration, setDuration ] = useState(30)
  const [ buffer, setBuffer ] = useState(0)
  const [ dateStart, setDateStart ] = useState("")
  const [ dateEnd, setDateEnd ] = useState("")
  const [ timeZone, setTimeZone ] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone)
  const [ availability, setAvailability ] = useState(
    Object.fromEntries(DAYS.map(d => [ d, [] ]))
  )

  useEffect(() => {
    fetchConfig()
  }, [])

  async function fetchConfig() {
    try {
      const res = await fetch(`/lti/courses/${courseId}/office_hours_config`, {
        headers: { "Accept": "application/json" }
      })
      if (res.ok) {
        const data = await res.json()
        setConfig(data)
        setDuration(data.meeting_duration)
        setBuffer(data.meeting_buffer)
        setDateStart(data.date_range_start)
        setDateEnd(data.date_range_end)
        setTimeZone(data.time_zone)
        setAvailability(data.weekly_availability)
      }
    } catch {
      // No config yet, use defaults
    } finally {
      setLoading(false)
    }
  }

  function toast(type, message) {
    window.dispatchEvent(new CustomEvent("app:toast", { detail: { type, message } }))
  }

  async function handleSave() {
    setSaving(true)

    const body = {
      meeting_duration: duration,
      meeting_buffer: buffer,
      date_range_start: dateStart,
      date_range_end: dateEnd,
      time_zone: timeZone,
      weekly_availability: availability
    }

    const method = config ? "PATCH" : "POST"
    const url = `/lti/courses/${courseId}/office_hours_config`

    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(body)
      })
      if (res.ok) {
        const data = await res.json()
        setConfig(data)
        toast("notice", "Availability saved")
      } else {
        const data = await res.json()
        toast("alert", data.errors?.join(", ") || data.error || "Failed to save")
      }
    } catch {
      toast("alert", "Network error")
    } finally {
      setSaving(false)
    }
  }

  function updateDayRange(day, index, newRange) {
    setAvailability(prev => ({
      ...prev,
      [day]: prev[day].map((r, i) => i === index ? newRange : r)
    }))
  }

  function addDayRange(day) {
    setAvailability(prev => ({
      ...prev,
      [day]: [ ...prev[day], { start: "09:00", end: "17:00" } ]
    }))
  }

  function removeDayRange(day, index) {
    setAvailability(prev => ({
      ...prev,
      [day]: prev[day].filter((_, i) => i !== index)
    }))
  }

  if (loading) return <div className="text-sm text-webex-gray-600 dark:text-webex-gray-400">Loading...</div>

  return (
    <div className="space-y-4">
      <Card padding="md" className="space-y-8">
        <div>
          <h3 className="text-sm font-semibold text-webex-gray-1000 dark:text-white mb-1">Meeting duration</h3>
          <p className="text-xs text-webex-gray-600 dark:text-webex-gray-400 mb-3">Set the duration for your office hours meetings</p>
          <div className="flex gap-2">
            {DURATIONS.map(d => (
              <Button
                key={d}
                type="button"
                variant={duration === d ? "primary" : "secondary"}
                size="default"
                onClick={() => setDuration(d)}
              >
                {d === 60 ? "1 hr" : `${d} min`}
              </Button>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-webex-gray-1000 dark:text-white mb-1">Meeting buffer</h3>
          <p className="text-xs text-webex-gray-600 dark:text-webex-gray-400 mb-3">Set a meeting buffer to block time before and after meetings</p>
          <div className="flex gap-2">
            {BUFFERS.map(b => (
              <Button
                key={b}
                type="button"
                variant={buffer === b ? "primary" : "secondary"}
                size="default"
                onClick={() => setBuffer(b)}
              >
                {BUFFER_LABELS[b]}
              </Button>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-webex-gray-1000 dark:text-white mb-1">Date range</h3>
          <p className="text-xs text-webex-gray-600 dark:text-webex-gray-400 mb-3">Set a range of dates when students can schedule office hours meetings</p>
          <div className="flex items-center gap-3">
            <div>
              <label className="text-xs text-webex-gray-600 dark:text-webex-gray-400 block mb-1">From</label>
              <Input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-webex-gray-600 dark:text-webex-gray-400 block mb-1">To</label>
              <Input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} />
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-webex-gray-1000 dark:text-white mb-1">Time zone</h3>
          <Select value={timeZone} onChange={(e) => setTimeZone(e.target.value)}>
            {Intl.supportedValuesOf("timeZone").map(tz => (
              <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>
            ))}
          </Select>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-webex-gray-1000 dark:text-white mb-1">Set availability</h3>
          <p className="text-xs text-webex-gray-600 dark:text-webex-gray-400 mb-3">Set the days and times you are available to host office hours each week</p>
          <div className="space-y-4">
            {DAYS.map((day, i) => (
              <div key={day} className="bg-webex-gray-50 dark:bg-webex-gray-900 rounded-md px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-webex-gray-1000 dark:text-white">{DAY_LABELS[i]}</span>
                  {availability[day].length === 0 && (
                    <span className="text-xs text-webex-gray-500 dark:text-webex-gray-400">Unavailable</span>
                  )}
                </div>
                <div className="space-y-2">
                  {availability[day].map((range, ri) => (
                    <TimeRangeInput
                      key={ri}
                      start={range.start}
                      end={range.end}
                      onChange={(newRange) => updateDayRange(day, ri, newRange)}
                      onRemove={() => removeDayRange(day, ri)}
                    />
                  ))}
                </div>
                <Button type="button" variant="text" size="default" onClick={() => addDayRange(day)} className="mt-2">
                  + Add availability
                </Button>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Button type="button" variant="primary" onClick={handleSave} disabled={saving}>
        {saving ? "Saving..." : "Save"}
      </Button>
    </div>
  )
}
